<template>
  <div class="min-h-screen bg-gray-50">
    <HeaderDefault />

    <!-- Hero Section -->
    <section class="bg-gradient-to-b from-primary to-primary/70 py-16">
      <div class="max-w-screen-xl mx-auto px-4">
        <div class="max-w-2xl">
          <h1 class="text-3xl md:text-4xl font-bold text-white mb-4">
            Submit an Event
          </h1>
          <p class="text-primary-light text-lg">
            Share academic events with our community. All submissions are
            reviewed for quality assurance.
          </p>
        </div>
      </div>
    </section>

    <!-- Form and Preview Section -->
    <section class="py-12">
      <div class="container mx-auto max-w-screen-xl px-4">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <!-- Form Column -->
          <div class="lg:col-span-8">
            <form @submit="addEvent" class="space-y-8">
              <div
                v-for="(section, sIndex) in form"
                :key="sIndex"
                class="bg-white rounded-xl shadow-sm p-6 space-y-6"
              >
                <h2
                  v-if="section.section"
                  class="text-xl font-semibold text-gray-900 pb-4 border-b"
                >
                  {{ section.section }}
                </h2>

                <div
                  v-for="(field, fIndex) in section.fields"
                  :key="fIndex"
                  class="space-y-2"
                >
                  <!-- Text, Email, URL Inputs -->
                  <template
                    v-if="['text', 'email', 'url'].includes(field.type)"
                  >
                    <label
                      :for="field.name"
                      class="block text-sm font-medium text-gray-700"
                    >
                      {{ field.label }}
                    </label>
                    <input
                      :id="field.name"
                      :type="field.type"
                      v-model="formv[field.name]"
                      :placeholder="field.placeholder"
                      :required="field.required"
                      class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    />
                  </template>

                  <!-- Textarea -->
                  <template v-else-if="field.type === 'textarea'">
                    <label
                      :for="field.name"
                      class="block text-sm font-medium text-gray-700"
                    >
                      {{ field.label }}
                    </label>
                    <textarea
                      :id="field.name"
                      v-model="formv[field.name]"
                      :placeholder="field.placeholder"
                      :required="field.required"
                      rows="5"
                      class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    ></textarea>
                  </template>

                  <!-- Date Input -->
                  <template v-else-if="field.type === 'date'">
                    <label
                      :for="field.name"
                      class="block text-sm font-medium text-gray-700"
                    >
                      {{ field.label }}
                    </label>
                    <input
                      :id="field.name"
                      type="date"
                      v-model="formv[field.name]"
                      :required="field.required"
                      class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    />
                  </template>

                  <!-- Submit Button -->
                  <template v-else-if="field.type === 'submit'">
                    <button
                      type="submit"
                      :disabled="isSubmitting"
                      class="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
                    >
                      <span>{{
                        isSubmitting ? "Submitting..." : "Submit Event"
                      }}</span>
                      <UIcon
                        v-if="isSubmitting"
                        name="i-heroicons-arrow-path"
                        class="w-5 h-5 animate-spin"
                      />
                      <UIcon
                        v-else
                        name="i-heroicons-paper-airplane"
                        class="w-5 h-5"
                      />
                    </button>
                  </template>
                </div>
              </div>
            </form>
          </div>

          <!-- Preview Column -->
          <div class="lg:col-span-4">
            <div class="lg:sticky lg:top-4">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
              <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                <div v-if="hasMinimumData" class="divide-y divide-gray-100">
                  <!-- Title -->
                  <div class="p-4">
                    <h3 class="text-xl font-semibold text-gray-900">
                      {{ formv.title || "Event Title" }}
                    </h3>
                  </div>

                  <!-- Dates -->
                  <div class="p-4 space-y-3">
                    <div class="flex items-center text-sm">
                      <icon
                        name="heroicons:calendar"
                        class="w-4 h-4 text-yellow-600 mr-2"
                      />
                      <span class="text-gray-600">Starts:</span>
                      <span class="ml-2 font-medium">
                        {{
                          formv.starts
                            ? new Date(formv.starts).toLocaleDateString()
                            : "Select start date"
                        }}
                      </span>
                    </div>
                    <div class="flex items-center text-sm">
                      <icon
                        name="heroicons:flag"
                        class="w-4 h-4 text-green-600 mr-2"
                      />
                      <span class="text-gray-600">Ends:</span>
                      <span class="ml-2 font-medium">
                        {{
                          formv.ends
                            ? new Date(formv.ends).toLocaleDateString()
                            : "Select end date"
                        }}
                      </span>
                    </div>
                  </div>

                  <!-- Location -->
                  <div class="p-4">
                    <div class="flex items-start">
                      <icon
                        name="heroicons:map-pin"
                        class="w-4 h-4 text-blue-600 mt-1 mr-2"
                      />
                      <div>
                        <span class="text-sm text-gray-600">Location:</span>
                        <p class="text-sm font-medium mt-1">
                          {{ formv.address || "Enter venue address" }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Description -->
                  <div class="p-4">
                    <div class="prose prose-sm max-w-none">
                      <p class="text-gray-600 line-clamp-3">
                        {{ formv.description || "Add event description..." }}
                      </p>
                    </div>
                  </div>

                  <!-- Registration Link -->
                  <div class="p-4">
                    <a
                      :href="formv.registration_url"
                      target="_blank"
                      class="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <icon name="heroicons:link" class="w-4 h-4 mr-1" />
                      {{ formv.registration_url || "Add registration link" }}
                    </a>
                  </div>
                </div>
                <div v-else class="text-gray-500 text-center py-8">
                  Fill in the form to see a preview of your event
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Success Modal -->
    <!-- Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 z-50">
          <!-- Backdrop -->
          <div
            class="absolute inset-0 bg-gray-900/75"
            @click="closeModal"
          ></div>

          <!-- Modal Content -->
          <div
            class="relative min-h-screen flex items-center justify-center p-4"
          >
            <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md">
              <div class="absolute right-4 top-4">
                <button
                  @click="closeModal"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <icon name="heroicons:x-mark" class="w-5 h-5" />
                </button>
              </div>

              <div class="p-6">
                <div class="text-center">
                  <icon
                    :name="
                      modalType === 'success'
                        ? 'heroicons:check-circle'
                        : 'heroicons:x-circle'
                    "
                    class="w-16 h-16 mx-auto mb-4"
                    :class="
                      modalType === 'success'
                        ? 'text-green-500'
                        : 'text-red-500'
                    "
                  />
                  <h3 class="text-xl font-semibold mb-2">
                    {{
                      modalType === "success"
                        ? "Event Submitted Successfully!"
                        : "Submission Failed"
                    }}
                  </h3>
                  <p class="text-gray-600 mb-6">
                    {{
                      modalType === "success"
                        ? "Your event has been submitted for review. We'll notify you once it's approved."
                        : "There was an error submitting your event. Please try again or contact support."
                    }}
                  </p>

                  <div class="flex gap-3 justify-center">
                    <button
                      @click="closeModal"
                      :class="
                        modalType === 'success' ? 'bg-primary' : 'bg-red-600'
                      "
                      class="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                    >
                      {{ modalType === "success" ? "Done" : "Try Again" }}
                    </button>
                    <button
                      v-if="modalType === 'error'"
                      @click="contactSupport"
                      class="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Contact Support
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <FooterDefault />
  </div>
</template>

<script setup lang="ts">
const formv = ref({});
const isSubmitting = ref(false);
const showSuccessModal = ref(false);
const showModal = ref(false);
const modalType = ref<"success" | "error">("success");

function closeModal() {
  showModal.value = false;
  if (modalType.value === "success") {
    navigateTo("/events");
  }
}

function contactSupport() {
  window.location.href = "mailto:academicalertshq@gmail.com";
}

async function addEvent(event: Event) {
  event.preventDefault();
  isSubmitting.value = true;

  try {
    const eventData = {
      ...formv.value,
      slug: generateSlug(formv.value.title),
      publishedAt: null,
      status: "draft",
    };

    const { data: resp } = await useFetch(
      "https://backend.postfeed.in/api/aa-events?status=draft",
      {
        method: "post",
        body: { data: eventData },
      }
    );

    if (resp.value?.data) {
      formv.value = {};
      modalType.value = "success";
      showModal.value = true;
    } else {
      throw new Error("Submission failed");
    }
  } catch (error) {
    modalType.value = "error";
    showModal.value = true;
  } finally {
    isSubmitting.value = false;
  }
}

// Compute if we have minimum data for preview
const hasMinimumData = computed(() => {
  return Object.keys(formv.value).length > 0;
});

const form = [
  {
    section: "Event Details",
    fields: [
      {
        label: "Event Title",
        type: "text",
        name: "title",
        placeholder: "Enter event title (e.g., Academic Conference 2024)",
        validation: "text",
        required: true,
      },
      {
        label: "Description",
        type: "textarea",
        name: "description",
        placeholder: "Provide a detailed description of your event...",
        validation: "text",
        required: true,
      },
      {
        label: "Start Date",
        type: "date",
        name: "starts",
        validation: "date",
        required: true,
      },
      {
        label: "End Date",
        type: "date",
        name: "ends",
        validation: "date",
        required: true,
      },
      {
        label: "Venue Address",
        type: "text",
        name: "address",
        placeholder: "Full venue address",
        validation: "text",
        required: true,
      },
      {
        label: "Registration Link",
        type: "url",
        name: "registration_url",
        placeholder: "https://",
        validation: "url",
        required: true,
      },
    ],
  },
  {
    section: "",
    fields: [
      {
        type: "submit",
        name: "submit",
      },
    ],
  },
];

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

useHead({
  title: "Submit an Event - Academic Alerts",
  meta: [
    {
      name: "description",
      content:
        "Submit your academic event for review and sharing with our community.",
    },
  ],
});
</script>
<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
